import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";

// In development, requests go through the Vite proxy (/api → VITE_API_BASE_URL)
// to avoid CORS issues. In production builds, hit the API host root directly
// (no /companion prefix; ingress path-routes backend vs companion profiles).
const baseURL = import.meta.env.DEV
  ? "/api"
  : import.meta.env.VITE_API_BASE_URL || "http://localhost:8003";

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  headers: { Accept: "application/json" },
});

export interface AuthInterceptorHandlers {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  refresh: () => Promise<string | null>;
  onLogout: () => void | Promise<void>;
}

export function installAuthInterceptorsOn(
  instance: AxiosInstance,
  handlers: AuthInterceptorHandlers,
): void {
  instance.interceptors.request.use((config) => {
    const requestUrl = String(config.url ?? "");
    const isAuthEndpoint =
      requestUrl.includes("/auth/google") ||
      requestUrl.includes("/auth/refresh") ||
      requestUrl.includes("/auth/logout");
    const token = handlers.getAccessToken();
    if (token && !isAuthEndpoint) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  let isRefreshing = false;
  const refreshQueue: Array<{
    resolve: (token: string | null) => void;
    reject: (err: unknown) => void;
  }> = [];

  function flushQueue(error: unknown, token: string | null = null): void {
    refreshQueue.forEach(({ resolve, reject }) => {
      if (error) reject(error);
      else resolve(token);
    });
    refreshQueue.length = 0;
  }

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config as
        | (AxiosRequestConfig & { _retried?: boolean })
        | undefined;
      const status = error.response?.status;

      if (
        status !== 401 ||
        !original ||
        original._retried ||
        !handlers.getRefreshToken()
      ) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token) => {
              if (!token) {
                reject(error);
                return;
              }
              original.headers = original.headers ?? {};
              (original.headers as Record<string, string>).Authorization =
                `Bearer ${token}`;
              resolve(instance.request(original));
            },
            reject,
          });
        });
      }

      original._retried = true;
      isRefreshing = true;

      try {
        const fresh = await handlers.refresh();
        if (!fresh) {
          flushQueue(error);
          await handlers.onLogout();
          return Promise.reject(error);
        }
        flushQueue(null, fresh);
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization =
          `Bearer ${fresh}`;
        return instance.request(original);
      } catch (refreshErr) {
        flushQueue(refreshErr);
        await handlers.onLogout();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    },
  );
}

let installed = false;
export function installAuthInterceptors(
  handlers: AuthInterceptorHandlers,
): void {
  if (installed) return;
  installed = true;
  installAuthInterceptorsOn(api, handlers);
}
